import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Breadcrumbs,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
  Autocomplete,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import api from "../axiosInstance";
import UIParamsConfig from "./UIParamsConfig";
import NavBar from "./NavBar";
import {
  TEXT_FIELD_STYLES,
  BUTTON_STYLES,
  FORM_STYLES,
  CARD_STYLES,
} from "../styles/componentStyles";
import { toast, ToastContainer } from "react-toastify";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
const BookForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = location.state?.isEdit || false;
  const bookData = location.state?.bookData || null;

  const [book, setBook] = useState({
    title: "",
    price: "",
    discount: "",
    rating: "",
    summary: "",
    abbreviation: "",
    aboutSubject: "",
    isbn: "",
    author: null,
    publisher: null,
    image: null,
    thumbnail: null,
    requestable: false,
    active: true,
    viewable: true,
    uiParams: {
      lmr: [
        {
          key: "faq",
          title: "LIST",
          source: "Question And Answers",
          visible: true,
        },
        {
          key: "pdf",
          title: "PDFs",
          source: "Question And Answers",
          visible: true,
        },
        {
          key: "exam-qp",
          title: "PYQS",
          source: "Exam QP",
          visible: true,
        },
        {
          key: "videos",
          title: "Videos",
          source: "Videos",
          visible: false,
        },
        {
          key: "tips-tricks",
          title: "Tips & Tricks",
          source: "Tips & Tricks",
          visible: false,
        },
        {
          key: "epub",
          title: "BOOK",
          source: "Question And Answers",
          visible: true,
        },
      ],
      topic_notes: [
        {
          key: "syllabus",
          title: "Syllabus",
          source: "Syllabus",
          visible: true,
        },
        {
          key: "topic-notes",
          title: "Notes",
          source: "Notes",
          visible: true,
        },
        {
          key: "internal-assessment",
          title: "Internal Assessments",
          source: "Internal Assessment",
          visible: true,
        },
      ],
      lmrAvailable: true,
      topicNotesAvailable: true,
    },
  });

  const [authors, setAuthors] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [allTitles, setAllTitles] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize data
  useEffect(() => {
    // Fetch authors and publishers
    api.get("/getAuthors").then((res) => setAuthors(res.data.data ?? []));
    api.get("/getPublishers").then((res) => setPublishers(res.data.data ?? []));
    api.get("/api/v1/allBooks").then((response) => {
      const titles = response.data.data.map((book) => book.title);
      setAllTitles(titles);
    });

    // Initialize book data for edit
    if (isEdit && bookData) {
      setBook({
        ...bookData,

        thumbnail: bookData.thumbnail ?? "",
        abbreviation: bookData.abbreviation ?? "",
        aboutSubject: bookData.aboutSubject ?? "",
        author: isEdit ? (bookData.authorName ?? "") : (bookData.author ?? ""),

        uiParams: bookData.uiParams ?? {
          lmr: [
            {
              key: "faq",
              title: "LIST",
              source: "Question And Answers",
              visible: true,
            },
            {
              key: "pdf",
              title: "PDFs",
              source: "Question And Answers",
              visible: true,
            },
            {
              key: "exam-qp",
              title: "PYQS",
              source: "Exam QP",
              visible: true,
            },
            {
              key: "videos",
              title: "Videos",
              source: "Videos",
              visible: false,
            },
            {
              key: "tips-tricks",
              title: "Tips & Tricks",
              source: "Tips & Tricks",
              visible: false,
            },
          ],
          topic_notes: [
            {
              key: "syllabus",
              title: "Syllabus",
              source: "Syllabus",
              visible: true,
            },
            {
              key: "topic-notes",
              title: "Notes",
              source: "Notes",
              visible: true,
            },
            {
              key: "internal-assessment",
              title: "Internal Assessments",
              source: "Internal Assessment",
              visible: true,
            },
          ],
          lmrAvailable: true,
          topicNotesAvailable: true,
        },
      });
    }
  }, [isEdit, bookData]);
  useEffect(() => {
    if (isEdit && bookData && publishers.length > 0) {
      const selectedPublisher =
        publishers.find((p) => p.id === bookData.publisherId) || null;

      setBook((prev) => ({
        ...prev,
        publisher: selectedPublisher,
      }));
    }
  }, [publishers]);
  const handleChange = useCallback((field, value) => {
    setBook((prev) => {
      const newBook = { ...prev, [field]: value };

      // If requestable is set to true, automatically set active and viewable to false
      if (field === "requestable" && value === true) {
        newBook.active = false;
        newBook.viewable = false;
      }

      return newBook;
    });
  }, []);

  const handleSuggestionClick = useCallback((suggestion) => {
    setBook((prev) => ({ ...prev, title: suggestion }));
    setSuggestions([]);
  }, []);

  const handleTitleChange = useCallback(
    (e) => {
      const value = e.target.value;
      handleChange("title", value);

      if (value.length >= 3) {
        const filteredSuggestions = allTitles
          .filter((bookTitle) =>
            bookTitle.toLowerCase().includes(value.toLowerCase()),
          )
          .slice(0, 5);
        setSuggestions(filteredSuggestions);
      } else {
        setSuggestions([]);
      }
    },
    [allTitles, handleChange],
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...book,
        authorId: isEdit
          ? bookData?.authorName?.id ||
            book?.authorName?.id ||
            book?.author?.id ||
            null
          : book?.author?.id || null,
        publisherId: book.publisher?.id || null,
        uiParams: book.uiParams,
        androidThumbnail: book.thumbnail,
      };

      const request = isEdit
        ? api.put(`/api/v1/books/${book.id}`, payload)
        : api.post("/api/v1/books", payload);
      console.log(payload);

      await request;

      toast.success(`Book ${isEdit ? "updated" : "created"} successfully!`);

      // Navigate back to book list
      setTimeout(() => {
        navigate("/bookdetails");
      }, 1500);
    } catch (error) {
      console.error("Save failed:", error);
      toast.error(
        `Failed to ${isEdit ? "update" : "create"} book. Please try again.`,
      );
    } finally {
      setIsSaving(false);
    }
  }, [book, bookData, isEdit, navigate]);

  const handleCancel = useCallback(() => {
    navigate("/bookdetails");
  }, [navigate]);

  // Thumbnail image upload in FTP
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [thumbnailType, setThumbnailType] = useState("upload");
  const [imagesUrl, setImagesUrl] = useState([]);
  const [deletingImage, setDeletingImage] = useState(false);
  const [thumbnailImage, setThumbnailImage] = useState(null);
  const [imageError, setImageError] = useState(null);
  const handleIconClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImageError("");
      console.log("File selected:", file.name);
    }
  };
  const handleUploadClick = async () => {
    if (!selectedFile) {
      console.log("No file selected!");
      return;
    }
    setLoadingImage(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await api.post(
        "/api/ftp/uploadBookThumbnail",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setThumbnailImage(response.data.path);
      setBook((prev) => ({
        ...prev,
        thumbnail: response.data.path,
      }));
      console.log("Upload success:", response.data);
      setLoadingImage(false);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 409) {
          setImageError(error.response.data.errorMessage);
          setLoadingImage(false);
        }
        console.error("Server responded with error:", error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Error setting up request:", error.message);
      }
    }
  };
  // Delete upload image record api fetch data
  const handleDelete = async () => {
    setDeletingImage(true);
    setLoadingImage(false);
    setImageError(false);
    try {
      await api.put(`/api/ftp/delete`, { path: thumbnailImage });
      setThumbnailImage("");
      setDeletingImage(false);
      setBook((prev) => ({
        ...prev,
        thumbnail: "",
      }));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Delete failed:", error);
      if (error.response.status === 400) {
        setImageError(error.response.data.errorMessage);
      }
    } finally {
      setDeletingImage(false);
    }
  };
  // Get iamge url api
  useEffect(() => {
    api
      .get("/api/ftp/getBookThumbnail")
      .then((response) => {
        setImagesUrl(response.data.files);
      })
      .catch((error) => {
        console.log("Error fecthing get api", error);
      })
      .finally(() => setLoadingImage(false));
  }, []);
  // Updating thumbnailType state
  useEffect(() => {
    if (isEdit === true) {
      setThumbnailType("select");
    } else {
      setThumbnailType("upload");
    }
  }, [isEdit]);
  return (
    <>
      {/* <NavBar /> */}
      <Box sx={{ p: 2 }}>
        <ToastContainer />

        {/* Header Section */}
        {/* <Card sx={{ mb: 2, boxShadow: 1 }}>
          <CardContent sx={{ p: 2 }}> */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 0.5 }}>
              <Link underline="hover" color="inherit" to="/bookdetails">
                Books List
              </Link>
              <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
                {isEdit ? "Edit Book" : "Add New Book"}
              </Typography>
            </Breadcrumbs>
            {/* <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              {isEdit ? "Edit Book" : "Add New Book"}
            </Typography> */}
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {isEdit
            ? "Update the book information and configuration below."
            : "Fill in the book details and configure the UI parameters below."}
        </Typography>
        {/* </CardContent>
        </Card> */}

        {/* Form Section */}
        <Card sx={{ boxShadow: 1 }}>
          <CardContent sx={{ p: 2 }}>
            <Box>
              {/* Two Column Layout */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: { xs: 2, md: 3 },
                  alignItems: "start",
                }}
              >
                {/* Left Column - Basic Information */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
                  >
                    Basic Information
                  </Typography>

                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {/* Book Title - Full Width */}
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        label="Book Title"
                        value={book.title}
                        onChange={handleTitleChange}
                        onBlur={() => setSuggestions([])}
                        {...TEXT_FIELD_STYLES.small}
                        fullWidth
                      />

                      {suggestions.length > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            boxShadow: 2,
                            maxHeight: 200,
                            overflowY: "auto",
                            mt: 0.5,
                          }}
                        >
                          {suggestions.map((suggestion, index) => (
                            <Box
                              key={index}
                              onMouseDown={() =>
                                handleSuggestionClick(suggestion)
                              }
                              sx={{
                                p: 1.5,
                                cursor: "pointer",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                  color: "primary.main",
                                },
                                "&:last-child": { borderBottom: "none" },
                              }}
                            >
                              {suggestion}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {/* Price, Discount, Rating - 3 columns */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "1fr 1fr",
                          md: "1fr 1fr 1fr",
                        },
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        label="Price"
                        type="number"
                        value={book.price}
                        onChange={(e) => handleChange("price", e.target.value)}
                        {...TEXT_FIELD_STYLES.small}
                      />
                      <TextField
                        label="Discount (%)"
                        type="number"
                        value={book.discount}
                        onChange={(e) =>
                          handleChange("discount", e.target.value)
                        }
                        {...TEXT_FIELD_STYLES.small}
                      />
                      <TextField
                        label="Rating"
                        type="number"
                        step="0.1"
                        value={book.rating}
                        onChange={(e) => handleChange("rating", e.target.value)}
                        {...TEXT_FIELD_STYLES.small}
                      />
                    </Box>

                    {/* ISBN - Full Width */}
                    <TextField
                      label="ISBN"
                      value={book.isbn}
                      onChange={(e) => handleChange("isbn", e.target.value)}
                      {...TEXT_FIELD_STYLES.small}
                      fullWidth
                    />

                    {/* Author and Publisher - 2 columns */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.5,
                      }}
                    >
                      {/* <FormControl {...TEXT_FIELD_STYLES.small}>
                        <InputLabel>Author</InputLabel>
                        <Select
                          label="Author"
                          value={book.author?.id || ""}
                          onChange={(e) => {
                            const selectedAuthor = authors.find(
                              (author) => author.id === e.target.value,
                            );
                            handleChange("author", selectedAuthor || null);
                          }}
                        >
                          {authors.map((author) => (
                            <MenuItem key={author.id} value={author.id}>
                              {author.firstName} {author.lastName}
                            </MenuItem>
                          ))}
                        </Select>
                        {isEdit && book.author && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, fontSize: "0.75rem" }}
                          >
                            Current: {book.author.firstName}{" "}
                            {book.author.lastName}
                          </Typography>
                        )}
                      </FormControl> */}
                      <TextField
                        label="Author"
                        value={book.author || ""}
                        onChange={(e) => handleChange("author", e.target.value)}
                        {...TEXT_FIELD_STYLES.small}
                      />
                      <FormControl {...TEXT_FIELD_STYLES.small}>
                        <InputLabel>Publisher</InputLabel>

                        <Select
                          label="Publisher"
                          value={book.publisher?.id || ""}
                          onChange={(e) => {
                            const selectedPublisher = publishers.find(
                              (publisher) => publisher.id === e.target.value,
                            );
                            handleChange(
                              "publisher",
                              selectedPublisher || null,
                            );
                          }}
                        >
                          {publishers.map((publisher) => (
                            <MenuItem key={publisher.id} value={publisher.id}>
                              {publisher.name}
                            </MenuItem>
                          ))}
                        </Select>

                        {isEdit && book.author && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, fontSize: "0.75rem" }}
                          >
                            Current: {book?.publisherId} {book?.publisherName}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    {/* Summary and About Subject - Full Width */}
                    <TextField
                      label="Summary"
                      multiline
                      rows={3}
                      value={book.summary}
                      onChange={(e) => handleChange("summary", e.target.value)}
                      {...TEXT_FIELD_STYLES.small}
                      fullWidth
                    />

                    <TextField
                      label="Abbreviation"
                      value={book.abbreviation}
                      onChange={(e) =>
                        handleChange("abbreviation", e.target.value)
                      }
                      {...TEXT_FIELD_STYLES.small}
                      fullWidth
                    />

                    <TextField
                      label="About Subject"
                      multiline
                      rows={3}
                      value={book.aboutSubject}
                      onChange={(e) =>
                        handleChange("aboutSubject", e.target.value)
                      }
                      {...TEXT_FIELD_STYLES.small}
                      fullWidth
                    />
                    <FormControl>
                      <FormLabel
                        component="legend"
                        sx={{
                          color: "#66667f !important",
                          fontWeight: 450,
                          textAlign: "left",
                        }}
                      >
                        Select Thumbnail Image Type
                      </FormLabel>

                      <RadioGroup
                        row
                        aria-labelledby="demo-form-control-label-placement"
                        name="position"
                        value={thumbnailType}
                        onChange={(e) => setThumbnailType(e.target.value)}
                      >
                        <FormControlLabel
                          value="upload"
                          control={<Radio />}
                          label="Upload Image"
                        />
                        <FormControlLabel
                          value="select"
                          control={<Radio />}
                          label="Select Image"
                        />
                      </RadioGroup>
                    </FormControl>
                    {thumbnailType == "upload" ? (
                      <>
                        <Box
                          sx={{ display: "flex", gap: 1, marginTop: "10px" }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            type="file"
                            inputRef={fileInputRef}
                            inputProps={{ accept: "image/*" }}
                            onChange={handleFileChange}
                          />

                          <Tooltip title="Upload image" arrow placement="top">
                            <IconButton
                              color="primary"
                              onClick={handleUploadClick}
                              size="small"
                              sx={{}}
                            >
                              <UploadFileIcon style={{ cursor: "pointer" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete image" arrow placement="top">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete()}
                              size="small"
                              sx={{}}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {/* SHOW NOTE ONLY WHEN NO MESSAGES ARE ACTIVE */}
                        {!loadingImage &&
                          !deletingImage &&
                          !imageError &&
                          !thumbnailImage && (
                            <Typography
                              sx={{
                                color: "#66667f",
                                fontSize: "13px",
                                fontStyle: "italic",
                                textAlign: "left",
                              }}
                            >
                              Note: After clicking the upload button, wait until
                              the image URL appears.
                            </Typography>
                          )}

                        {/* ERROR OR STATUS MESSAGES */}
                        {imageError ? (
                          <Typography
                            sx={{
                              color: "red",
                              fontSize: "14px",
                              textAlign: "left",
                            }}
                          >
                            {imageError}
                          </Typography>
                        ) : (
                          <Typography
                            sx={{
                              color: "#66667f",
                              fontSize: "13px",
                              fontStyle: "italic",
                              textAlign: "left",
                            }}
                          >
                            {loadingImage && " ...Uploading Image"}
                            {deletingImage && " ...Deleting Image"}
                            {!loadingImage &&
                              !deletingImage &&
                              thumbnailImage && (
                                <>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Typography
                                      sx={{ fontSize: "13px", color: "#666" }}
                                    >
                                      Image Url: {thumbnailImage}
                                    </Typography>

                                    <Box
                                      component="img"
                                      src={`https://siaedutech.com/public/${thumbnailImage}`}
                                      alt="preview"
                                      sx={{
                                        width: "70px",
                                        borderRadius: 1,
                                        border: "1px solid #ddd",
                                        ml: 1,
                                      }}
                                    />
                                  </Box>
                                </>
                              )}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <FormControl
                        size="small"
                        fullWidth
                        // error={
                        //   formik.touched.active && Boolean(formik.errors.active)
                        // }
                        sx={{ mt: 2 }}
                      >
                        <InputLabel>Image URL</InputLabel>
                        <Select
                          label="Image URL"
                          labelId="image-select-label"
                          id="image"
                          value={book.thumbnail ?? ""}
                          onChange={(e) =>
                            handleChange("thumbnail", e.target.value)
                          }
                          // onBlur={formik.handleBlur}
                          // error={
                          //   formik.touched.image && Boolean(formik.errors.image)
                          // }
                          // helperText={
                          //   formik.touched.image && formik.errors.image
                          // }
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: "300px",
                                overflowY: "auto",
                              },
                            },
                          }}
                          sx={{
                            "& .MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            },
                          }}
                        >
                          <MenuItem value="" sx={{ fontStyle: "italic" }}>
                            None
                          </MenuItem>
                          {imagesUrl.map((img, index) => (
                            <MenuItem
                              value={img}
                              key={index}
                              sx={{
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                },
                              }}
                            >
                              <Box
                                component="img"
                                src={`https://siaedutech.com/public/${img}`}
                                alt="preview"
                                sx={{
                                  width: "35px",
                                  borderRadius: 1,
                                  border: "1px solid #ddd",
                                }}
                              />{" "}
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#555",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {img}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                        {/*
                        {formik.touched.image && formik.errors.image && (
                          <FormHelperText sx={{ color: "red" }}>
                            {formik.errors.image}
                          </FormHelperText>
                        )} */}
                      </FormControl>
                      // <TextField
                      //   label="Thumbnail"
                      //   value={book.thumbnail ?? ""}
                      //   onChange={(e) =>
                      //     handleChange("thumbnail", e.target.value)
                      //   }
                      //   {...TEXT_FIELD_STYLES.small}
                      //   fullWidth
                      // />
                    )}
                  </Box>
                  {/* Status Settings */}
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{
                      mt: 2,
                      mb: 2,
                      color: "primary.main",
                      fontWeight: 600,
                    }}
                  >
                    Status Settings
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      mb: 3,
                    }}
                  >
                    <FormControl {...TEXT_FIELD_STYLES.small}>
                      <InputLabel>Requestable</InputLabel>
                      <Select
                        label="Requestable"
                        value={
                          book.requestable !== undefined
                            ? book.requestable
                            : false
                        }
                        onChange={(e) =>
                          handleChange(
                            "requestable",
                            e.target.value === "true" ||
                              e.target.value === true,
                          )
                        }
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                      {book.requestable && (
                        <Typography
                          variant="caption"
                          color="info.main"
                          sx={{ mt: 0.5, fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          When requestable, Active and Viewable will be
                          automatically set to "No"
                        </Typography>
                      )}
                    </FormControl>

                    <FormControl {...TEXT_FIELD_STYLES.small}>
                      <InputLabel>Active</InputLabel>
                      <Select
                        label="Active"
                        value={book.active !== undefined ? book.active : true}
                        disabled={book.requestable === true}
                        onChange={(e) =>
                          handleChange(
                            "active",
                            e.target.value === "true" ||
                              e.target.value === true,
                          )
                        }
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                      {book.requestable && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ mt: 0.5, fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          ⚠️ Automatically set to "No" when Requestable is "Yes"
                        </Typography>
                      )}
                    </FormControl>

                    <FormControl {...TEXT_FIELD_STYLES.small}>
                      <InputLabel>Viewable</InputLabel>
                      <Select
                        label="Viewable"
                        value={
                          book.viewable !== undefined ? book.viewable : true
                        }
                        disabled={book.requestable === true}
                        onChange={(e) =>
                          handleChange(
                            "viewable",
                            e.target.value === "true" ||
                              e.target.value === true,
                          )
                        }
                      >
                        <MenuItem value={true}>Yes</MenuItem>
                        <MenuItem value={false}>No</MenuItem>
                      </Select>
                      {book.requestable && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ mt: 0.5, fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          ⚠️ Automatically set to "No" when Requestable is "Yes"
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                </Box>

                {/* Right Column - Status Settings and UI Parameters */}
                <Box>
                  {/* UI Parameters Configuration */}
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
                  >
                    UI Parameters Configuration
                  </Typography>

                  <UIParamsConfig
                    value={book.uiParams}
                    onChange={useCallback(
                      (uiParams) => handleChange("uiParams", uiParams),
                      [handleChange],
                    )}
                    disabled={false}
                  />
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 2,
                pt: 2,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                onClick={handleCancel}
                {...BUTTON_STYLES.small}
                disabled={isSaving}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                {...BUTTON_STYLES.small}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={16} /> : null}
                sx={{ minWidth: 120 }}
              >
                {isSaving
                  ? "Saving..."
                  : isEdit
                    ? "Update Book"
                    : "Create Book"}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default BookForm;
